import { create } from 'zustand';
import { apiFetch, setSessionLostHandler } from '../lib/apiClient';

export type Role = 'admin' | 'user';

export interface User {
  sub: string;
  email: string;
  name: string;
  role: Role;
  /** Outils effectifs (un admin les a tous). */
  tools: string[];
  isSuperAdmin: boolean;
}

export interface TranscribeUsage {
  usedSec: number;
  /** null = illimité */
  quotaMin: number | null;
}

type Status = 'loading' | 'authenticated' | 'anonymous';

interface AuthState {
  status: Status;
  user: User | null;
  transcribe: TranscribeUsage | null;
  /** Session en cours (cookies HttpOnly) : rien n'est stocké côté navigateur. */
  load: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  activate: (username: string, code: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  logoutEverywhere: () => Promise<void>;
  setUser: (user: User) => void;
}

type UserResponse = { user: User };

export const useAuthStore = create<AuthState>()((set) => ({
  status: 'loading',
  user: null,
  transcribe: null,

  load: async () => {
    try {
      const res = await apiFetch<{ user: User; transcribe: TranscribeUsage | null }>('/auth/me');
      set({ status: 'authenticated', user: res.user, transcribe: res.transcribe });
    } catch {
      set({ status: 'anonymous', user: null, transcribe: null });
    }
  },

  login: async (email, password) => {
    const res = await apiFetch<UserResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    set({ status: 'authenticated', user: res.user });
  },

  activate: async (username, code, password) => {
    const res = await apiFetch<UserResponse>('/auth/activate', {
      method: 'POST',
      body: JSON.stringify({ username, code, password }),
    });
    set({ status: 'authenticated', user: res.user });
  },

  logout: async () => {
    await apiFetch<void>('/auth/logout', { method: 'POST' }).catch(() => {});
    set({ status: 'anonymous', user: null, transcribe: null });
  },

  logoutEverywhere: async () => {
    await apiFetch<void>('/auth/logout-all', { method: 'POST' });
    set({ status: 'anonymous', user: null, transcribe: null });
  },

  setUser: (user) => set({ user }),
}));

setSessionLostHandler(() => {
  useAuthStore.setState({ status: 'anonymous', user: null, transcribe: null });
});

export const isAdmin = (user: User | null) => user?.role === 'admin';

export function initials(user: Pick<User, 'name' | 'email'>): string {
  const source = user.name.trim() || user.email.split('@')[0];
  const parts = source.split(/[\s._-]+/).filter(Boolean);
  const letters = parts.length > 1 ? parts[0][0] + parts[parts.length - 1][0] : source.slice(0, 2);
  return letters.toUpperCase();
}

// Ancien état persistant (mot de passe partagé) : plus utilisé.
try {
  localStorage.removeItem('auth-storage');
} catch {
  /* stockage indisponible */
}
